import React from 'react';
import moment from 'moment';
import { Button, Card, Stack, Typography } from '@mui/material';
import { Control } from 'react-hook-form/dist/types/form';
import { AutocompleteField, DateRangeWithPeriods } from 'components/form';

import { LenderPerformanceFilterFormValues } from '../../../types';
import { useLenderPerformanceFilterOptions } from '../../../hooks/useLenderPerformanceFilterOptions';

interface Props {
  onReset: VoidFunction;
  control: Control<LenderPerformanceFilterFormValues>;
  onRangeChange: (range: { endDate: moment.Moment; startDate: moment.Moment }) => Promise<void>;
}

export const LenderPerformanceFilters = ({ control, onRangeChange, onReset }: Props) => {
  const { lenderOptions, salesRepOptions, locationOptions, isLocationsLoading } = useLenderPerformanceFilterOptions();

  const filterFieldSx = {
    flex: '1 1 16rem',
    minWidth: {
      xs: '100%',
      sm: '16rem',
    },
    '& .MuiAutocomplete-inputRoot': {
      flexWrap: 'nowrap',
      overflow: 'hidden',
    },
    '& .MuiAutocomplete-tag': {
      maxWidth: {
        xs: '12rem',
        sm: '6rem',
        md: '4rem',
        lg: '8rem',
        xl: '16rem',
      },
    },
  };

  return (
    <Card>
      <Stack gap="0.5rem" padding="1rem">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography component="span" variant="h6">
            Filters
          </Typography>

          <Button size="small" variant="contained" onClick={onReset}>
            Reset
          </Button>
        </Stack>

        <Stack direction="row" gap="0.5rem" flexWrap="wrap">
          <AutocompleteField
            multiple
            size="small"
            limitTags={2}
            name="lenders"
            control={control}
            sx={filterFieldSx}
            disableCloseOnSelect
            options={lenderOptions}
            textFieldProps={{
              size: 'small',
              label: 'Lenders',
              variant: 'outlined',
            }}
          />

          <AutocompleteField
            multiple
            size="small"
            limitTags={2}
            name="salesReps"
            control={control}
            sx={filterFieldSx}
            disableCloseOnSelect
            options={salesRepOptions}
            textFieldProps={{
              size: 'small',
              variant: 'outlined',
              label: 'Sales reps',
            }}
          />

          <AutocompleteField
            multiple
            size="small"
            limitTags={2}
            name="locations"
            control={control}
            sx={filterFieldSx}
            disableCloseOnSelect
            options={locationOptions}
            isLoading={isLocationsLoading}
            textFieldProps={{
              size: 'small',
              variant: 'outlined',
              label: 'Locations',
            }}
          />
        </Stack>

        <DateRangeWithPeriods
          nameTo="to"
          size="small"
          nameFrom="from"
          control={control}
          variant="outlined"
          onRangeChange={onRangeChange}
        />
      </Stack>
    </Card>
  );
};
